import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IdCard, Image as ImageIcon, ShieldCheck } from "lucide-react";
import { adminApi } from "../utils/adminApi";
import "./TrackRepair.css";

const TRACK_REQUEST_TIMEOUT_MS = 90000;

const TrackRepair = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { trackingId: urlTrackingId, phone: urlPhone } = useParams();
  const trackToken = new URLSearchParams(location.search).get("t") || "";
  const didAutoTrackRef = useRef(false);

  // Auto-fill from URL OR navigate state OR empty.
  const [trackingId, setTrackingId] = useState(
    urlTrackingId || location.state?.trackingId || ""
  );
  const [phone, setPhone] = useState(urlPhone || location.state?.phone || "");

  // Show instant data when navigating from Booking page, then refresh from API.
  const [booking, setBooking] = useState(location.state?.prefillBooking || null);
  const [successMessage, setSuccessMessage] = useState(
    location.state?.fromBooking
      ? "Booking created successfully. Fetching latest status..."
      : trackToken
        ? "Opening your secure tracking link..."
      : ""
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpMessage, setOtpMessage] = useState("");
  const [otpError, setOtpError] = useState("");

  const fetchBooking = useCallback(
    async ({ preserveBooking = false, preferToken = false } = {}) => {
      const normalizedTrackingId = trackingId.trim().toUpperCase();
      const normalizedPhone = phone.trim();
      const shouldUseToken = preferToken && Boolean(trackToken);

      if (!shouldUseToken && (!normalizedTrackingId || !normalizedPhone)) {
        setError("Booking ID and mobile number are required.");
        return;
      }

      try {
        setLoading(true);
        setError("");
        if (!preserveBooking) {
          setBooking(null);
        }

        const response = shouldUseToken
          ? await adminApi.get("/api/bookings/track-link", {
              params: { t: trackToken },
              timeout: TRACK_REQUEST_TIMEOUT_MS,
            })
          : await adminApi.post(
              "/api/bookings/track",
              {
                trackingId: normalizedTrackingId,
                phone: normalizedPhone,
              },
              {
                timeout: TRACK_REQUEST_TIMEOUT_MS,
              }
            );

        setBooking(response.data);
        if (response.data?.trackingId) {
          setTrackingId(response.data.trackingId);
        }
        if (response.data?.phone) {
          setPhone(response.data.phone);
        }
        setSuccessMessage(
          location.state?.fromBooking
            ? "Booking created successfully."
            : shouldUseToken
              ? "Tracking opened from secure link."
              : ""
        );
      } catch (err) {
        if (err.code === "ECONNABORTED") {
          setError("Tracking is taking longer than expected. Please try again.");
        } else {
          setError(
            err.response?.data?.error || "Tracking failed. Please verify details."
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [trackingId, phone, location.state, trackToken]
  );

  // Auto track once when opened with secure token.
  useEffect(() => {
    if (trackToken && !didAutoTrackRef.current) {
      didAutoTrackRef.current = true;
      fetchBooking({ preserveBooking: true, preferToken: true });
    }
  }, [trackToken, fetchBooking]);

  // Auto track once when opened with URL params.
  useEffect(() => {
    if (!trackToken && urlTrackingId && urlPhone && !didAutoTrackRef.current) {
      didAutoTrackRef.current = true;
      fetchBooking({ preserveBooking: true });
    }
  }, [trackToken, urlTrackingId, urlPhone, fetchBooking]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage("");
    fetchBooking();
  };

  const handleClose = () => {
    const shouldGoHome = Boolean(trackToken) || Boolean(location.state?.fromBooking);
    if (shouldGoHome) {
      navigate("/", { replace: true });
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/", { replace: true });
  };

  const handleVerifyOtp = async () => {
    if (!booking) return;
    if (!booking.technicianName && !booking.technician && !booking.technicianId) {
      setOtpError("Technician is not assigned yet.");
      return;
    }

    const cleanOtp = String(otp).replace(/\D/g, "").slice(0, 6);
    if (!/^\d{4,6}$/.test(cleanOtp)) {
      setOtpError("Enter a valid OTP.");
      return;
    }

    setOtpLoading(true);
    setOtpError("");
    setOtpMessage("");

    try {
      await adminApi.post(
        "/api/bookings/verify-visit-otp",
        {
          trackingId: booking.trackingId || booking._id,
          phone: booking.phone,
          otp: cleanOtp,
        },
        { timeout: TRACK_REQUEST_TIMEOUT_MS }
      );

      // setOtpMessage("OTP verified. Technician identity confirmed.");
      setTimeout(() => {
  navigate("/thank-you", {
    state: {
      trackingId: booking.trackingId,
      technician: booking.technicianName,
    },
  });
}, 1500);
    } catch (err) {
      setOtpError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "OTP verification failed. Please try again."
      );
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div className="track-page">
      <div className="track-card">
        <div className="track-header">
          <h1>Track Your Booking</h1>
          <button
            type="button"
            className="track-close-btn"
            onClick={handleClose}
            aria-label="Close tracking"
            title="Close"
          >
            ×
          </button>
        </div>
        <p>
          {trackToken
            ? "Secure link loaded. You can still track manually using Booking ID and mobile."
            : "Enter Booking ID and mobile number to check current repair status."}
        </p>
        <form onSubmit={handleSubmit} className="track-form">
          <input
            type="text"
            placeholder="Booking ID (Example: SB-ABC123)"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            required
            style={{ textTransform: "uppercase" }}
          />

          <input
            type="tel"
            placeholder="Mobile Number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
          />

          <button type="submit" disabled={loading}>
            {loading ? "Tracking..." : "Track Booking"}
          </button>
        </form>

        {successMessage && <p className="track-success">{successMessage}</p>}
        {error && <p className="track-error">{error}</p>}

        {booking && (
          <div className="track-result">
            <div>
              <strong>Booking ID:</strong> {booking.trackingId || booking._id}
            </div>
            <div>
              <strong>Device:</strong> {booking.brand} {booking.model}
            </div>
            <div>
              <strong>Issue:</strong> {booking.service}
            </div>
            <div>
              <strong>Status:</strong> {booking.status || "Pending"}
            </div>
            <div>
              <strong>Pickup Type:</strong> {booking.pickupOption}
            </div>
            <div>
              <strong>Address:</strong> {booking.address}
            </div>
            <div>
              <strong>Technician:</strong> {booking.technicianName || "Not Assigned"}
            </div>
            <div>
              <strong>Technician Phone:</strong> {booking.technicianPhone || "-"}
            </div>

            <div className="track-tech-identity">
              <div className="track-tech-avatar" aria-hidden="true">
                {booking.technicianPhoto ? (
                  <img src={booking.technicianPhoto} alt="Technician" />
                ) : (
                  <ImageIcon size={22} />
                )}
              </div>

              <div className="track-tech-meta">
                <div className="track-tech-name">
                  <span>
                    {booking.technicianName || booking.technician || "Technician not assigned"}
                  </span>
                  {booking.technicianVerified ? (
                    <span className="track-tech-badge">
                      <ShieldCheck size={14} /> Verified
                    </span>
                  ) : null}
                </div>

                <div className="track-tech-id">
                  <IdCard size={14} /> ID: {booking.technicianEmployeeId || "Pending"}
                </div>
              </div>
            </div>

            <div className="track-otp">
              <div className="track-otp-title">OTP check (pickup safety)</div>
              <div className="track-otp-row">
                <input
                  type="text"
                  placeholder="Enter OTP shown by technician"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  maxLength={6}
                />
                <button type="button" onClick={handleVerifyOtp} disabled={otpLoading}>
                  {otpLoading ? "Verifying..." : "Verify OTP"}
                </button>
              </div>

              {otpMessage ? <p className="track-success">{otpMessage}</p> : null}
              {otpError ? <p className="track-error">{otpError}</p> : null}
            </div>
            <div>
              <strong>Last Updated:</strong>{" "}
              {new Date(booking.updatedAt || booking.createdAt).toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackRepair;
